import React, { useState } from 'react';
import RocketTrajectorySimulator from './RocketTrajectorySimulator';

const Games = () => {
  const [selectedGame, setSelectedGame] = useState(null);

  // Game data - we'll expand this as we add games
  const games = [
    {
      id: 'rocket-trajectory',
      title: 'Rocket Trajectory Simulator',
      description: 'Launch rockets and calculate perfect orbital trajectories! Master the physics of spaceflight by hitting target orbits.',
      icon: '🚀',
      difficulty: 'Medium',
      educationalFocus: 'Orbital Mechanics & Physics',
      status: 'Ready to Play',
      component: RocketTrajectorySimulator
    },
    {
      id: 'coming-soon',
      title: 'More Games Coming Soon!',
      description: 'Exciting space-themed educational games are being developed. Stay tuned for fun learning experiences!',
      icon: '🌌',
      difficulty: 'Various',
      educationalFocus: 'Multiple Space Topics',
      status: 'In Development'
    }
  ];

  const openGame = (gameId) => {
    setSelectedGame(gameId);
  };

  const closeGame = () => {
    setSelectedGame(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-900 via-blue-900 to-black text-white">
      {/* Header */}
      <div className="relative overflow-hidden pt-2">
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="relative z-10 p-8 text-center">
          <h1 className="text-5xl font-bold mb-4 text-purple-400">🚀 Space Games Hub</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Blast off into fun! Play educational games that teach you about space, planets, rockets, and the universe while having a great time.
          </p>
        </div>
      </div>

      {/* Games Grid */}
      <div className="container mx-auto px-4 pb-8 pt-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {games.map((game) => (
            <div
              key={game.id}
              className="bg-black bg-opacity-50 rounded-lg p-6 hover:bg-opacity-70 transition-all duration-300 cursor-pointer transform hover:scale-105"
              onClick={() => openGame(game.id)}
            >
              <div className="text-center mb-4">
                <div className="text-6xl mb-4">{game.icon}</div>
                <h3 className="text-2xl font-bold text-purple-400 mb-2">{game.title}</h3>
                <p className="text-gray-300 mb-4">{game.description}</p>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-400">Difficulty:</span>
                  <span className="text-white">{game.difficulty}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Learning:</span>
                  <span className="text-white">{game.educationalFocus}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Status:</span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    game.status === 'Ready to Play' ? 'bg-green-600' : 'bg-yellow-600'
                  }`}>
                    {game.status}
                  </span>
                </div>
              </div>

              <div className="mt-4 text-center">
                <button
                  className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                    game.status === 'Ready to Play'
                      ? 'bg-purple-600 hover:bg-purple-700 text-white'
                      : 'bg-gray-600 text-gray-300 cursor-not-allowed'
                  }`}
                  disabled={game.status !== 'Ready to Play'}
                >
                  {game.status === 'Ready to Play' ? 'Play Now!' : 'Coming Soon'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Educational Message */}
        <div className="mt-12 bg-black bg-opacity-50 rounded-lg p-8 text-center">
          <h2 className="text-3xl font-bold text-purple-400 mb-4">🎓 Learning Through Play</h2>
          <p className="text-gray-300 text-lg mb-6 max-w-4xl mx-auto">
            Our games are designed to make learning about space fun and engaging! Each game teaches real scientific concepts
            while providing an enjoyable gaming experience. From understanding rocket physics to exploring planetary systems,
            you'll learn while you play.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            <div className="bg-gray-800 rounded-lg p-6">
              <div className="text-4xl mb-3">🧠</div>
              <h3 className="text-xl font-semibold text-purple-400 mb-2">Educational Content</h3>
              <p className="text-gray-300">Games teach real space science concepts and facts</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-6">
              <div className="text-4xl mb-3">🎮</div>
              <h3 className="text-xl font-semibold text-purple-400 mb-2">Fun Gameplay</h3>
              <p className="text-gray-300">Engaging mechanics that keep kids interested and motivated</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-6">
              <div className="text-4xl mb-3">🌟</div>
              <h3 className="text-xl font-semibold text-purple-400 mb-2">Progressive Learning</h3>
              <p className="text-gray-300">Games get more challenging as skills and knowledge increase</p>
            </div>
          </div>
        </div>
      </div>

      {/* Game Modal */}
      {selectedGame && (() => {
        const gameData = games.find(g => g.id === selectedGame);
        if (gameData && gameData.component) {
          const GameComponent = gameData.component;
          return <GameComponent onClose={closeGame} />;
        } else {
          return (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
              <div className="bg-gray-800 rounded-lg p-8 max-w-md w-full text-center">
                <h3 className="text-2xl font-bold text-purple-400 mb-4">🚀 Game Coming Soon!</h3>
                <p className="text-gray-300 mb-6">
                  We're working hard to bring you amazing space-themed educational games.
                  Check back soon for the first game launch!
                </p>
                <button
                  onClick={closeGame}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          );
        }
      })()}
    </div>
  );
};

export default Games;
